import React, { useMemo, useState } from 'react';

import {
  PAYMENTS_FILTER_ALL,
  PAYMENTS_FILTER_CHIPS,
  PAYMENT_MODE_LABELS,
} from '../../../constants/admission';
import { formatPaise } from '../../../utils/money';
import { buildDepositsModel, isFullyRefunded } from './billModel';

/**
 * DepositsSection (C4 · S2/S5) — the deposits + refunds list rendered as a
 * SECTION inside the running bill (recommended variant: payments live in-bill,
 * one money story in one place). It is the single S5 surface: rows per receipt
 * (no. · date · mode · by/reason · amount · running deposits-held), refunds
 * nested under their parent, Print + Refund per row, Add-deposit pinned at TOP
 * (never a form under an unbounded list), Deposits/Refunds filter + search past
 * a long list, collapse beyond the latest N, empty state, and the footer money
 * totals (all from the server's cached figures — no re-derive).
 *
 * Doctor/nurse read-only: no Add/Refund affordances (perm-gated by the parent).
 * All projection/collapse/filter logic lives in `buildDepositsModel` (single
 * source). This component only renders and owns transient filter/search UI state.
 */
const chipOn = 'text-[12px] font-semibold px-3 py-1 rounded-full border bg-teal-600 border-teal-600 text-white';
const chipOff = 'text-[12px] font-semibold px-3 py-1 rounded-full border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700';
const smallBtn = 'px-2.5 py-1 text-[12px] rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100 hover:bg-slate-100 dark:hover:bg-slate-700 disabled:opacity-60';

/** ISO "…T…+05:30" → "12 Jul, 11:42 am" (org-local; the date/time are already IST). */
function formatReceiptDateTime(iso) {
  if (!iso || typeof iso !== 'string') return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  let h = d.getHours();
  const m = String(d.getMinutes()).padStart(2, '0');
  const ampm = h >= 12 ? 'pm' : 'am';
  h = h % 12 || 12;
  return `${d.getDate()} ${MONTHS[d.getMonth()]}, ${h}:${m} ${ampm}`;
}

function PaymentRow({ row, canRefund, canPrint, onRefund, onPrint }) {
  const refund = row.isRefund;
  const magnitudePaise = Math.abs(Number(row.amount_paise) || 0);
  const fullyRefunded = !refund && isFullyRefunded(row);
  return (
    <div
      className={`flex items-center gap-2.5 px-4 py-2 text-[13px] ${refund ? 'border-l-[3px] border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-900/20 pl-6' : 'hover:bg-slate-50 dark:hover:bg-slate-800/60'}`}
      data-testid={`payment-row-${row.payment_id}`}
    >
      <span className={`w-28 font-semibold tabular-nums flex-none ${refund ? 'text-red-600 dark:text-red-400' : 'text-slate-900 dark:text-white'}`}>{row.receipt_number || '—'}</span>
      <span className="w-24 text-[12px] text-slate-500 dark:text-slate-400 flex-none hidden sm:block">{formatReceiptDateTime(row.collected_at)}</span>
      <span className="w-16 flex-none hidden md:block">
        <span className="inline-flex items-center text-[11px] font-semibold px-2 py-0.5 rounded-full border border-slate-300 dark:border-slate-600 text-slate-600 dark:text-slate-300">{PAYMENT_MODE_LABELS[row.method] || row.method}</span>
      </span>
      <span className="flex-1 min-w-0 text-[12px] text-slate-500 dark:text-slate-400 truncate">
        {row.collected_by_role || '—'}
        {refund && row.reason ? ` · “${row.reason}”` : ''}
        {!refund && Number(row.refunded_total_paise) > 0 && !fullyRefunded ? ` · ${formatPaise(row.refunded_total_paise)} refunded` : ''}
        {fullyRefunded ? ' · Fully refunded' : ''}
      </span>
      <span className={`w-24 text-right font-semibold tabular-nums flex-none ${refund ? 'text-red-600 dark:text-red-400' : 'text-teal-700 dark:text-teal-300'}`}>
        {refund ? '− ' : '+ '}{formatPaise(magnitudePaise)}
      </span>
      <span className="w-24 text-right text-[12px] text-slate-500 dark:text-slate-400 tabular-nums flex-none hidden lg:block">{row.heldPaise != null ? formatPaise(row.heldPaise) : ''}</span>
      <span className="w-32 text-right flex-none flex gap-1.5 justify-end">
        {canPrint && (
          <button type="button" className={smallBtn} onClick={() => onPrint(row)} data-testid={`payment-print-${row.payment_id}`}>
            {refund ? 'Print voucher' : 'Print'}
          </button>
        )}
        {!refund && canRefund && (
          <button
            type="button"
            className={smallBtn}
            onClick={() => onRefund(row)}
            disabled={fullyRefunded}
            title={fullyRefunded ? 'Fully refunded' : 'Refund this deposit'}
            data-testid={`payment-refund-${row.payment_id}`}
          >Refund deposit</button>
        )}
      </span>
    </div>
  );
}

export default function DepositsSection({
  bill, canCollect, canRefund, canPrint, onCollect, onRefund, onPrint,
}) {
  const [filter, setFilter] = useState(PAYMENTS_FILTER_ALL);
  const [query, setQuery] = useState('');
  const [showAll, setShowAll] = useState(false);

  const model = useMemo(
    () => buildDepositsModel({ payments: bill.payments, filter, query, showAll }),
    [bill.payments, filter, query, showAll],
  );

  const depositTotalPaise = Number(bill.deposit_total_paise) || 0;
  const refundedPaise = Number(bill.refunded_deposits_paise) || 0;
  const netHeldPaise = Number(bill.total_deposits_paise) || 0;

  return (
    <div className="border-t border-slate-200 dark:border-slate-700" data-testid="deposits-section">
      {/* Tools + pinned Add-deposit (ALWAYS above the list) */}
      <div className="flex items-center gap-2 flex-wrap px-4 py-3 border-b border-slate-200 dark:border-slate-700">
        <span className="text-[13px] font-bold text-slate-800 dark:text-slate-100">Deposits &amp; refunds</span>
        {PAYMENTS_FILTER_CHIPS.map((c) => {
          const count = c.key === PAYMENTS_FILTER_ALL ? (model.depositCount + model.refundCount)
            : c.key === 'IPD_DEPOSIT' ? model.depositCount : model.refundCount;
          return (
            <button key={c.key} type="button" onClick={() => setFilter(c.key)} className={filter === c.key ? chipOn : chipOff} data-testid={`payments-filter-${c.key}`}>
              {c.label} · {count}
            </button>
          );
        })}
        {model.showSearch && (
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search receipt no. / mode…"
            className="flex-1 min-w-[140px] max-w-[240px] px-2.5 py-1.5 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-100 text-[12.5px]"
            data-testid="payments-search"
          />
        )}
        {canCollect && (
          <button type="button" onClick={onCollect} className="ml-auto px-3 py-1.5 rounded-lg bg-teal-600 text-white text-[12.5px] font-semibold" data-testid="take-deposit-open">+ Add deposit</button>
        )}
      </div>

      {model.isEmpty ? (
        <div className="p-6 text-center" data-testid="deposits-empty">
          <div className="text-2xl mb-1">🧾</div>
          <div className="font-semibold text-slate-900 dark:text-white">No deposits yet</div>
          <div className="text-[12.5px] text-slate-500 dark:text-slate-400 mt-1">
            {canCollect ? 'Collect the first advance with “+ Add deposit” above.' : 'No advance deposits have been collected on this admission.'}
          </div>
        </div>
      ) : model.matchedCount === 0 ? (
        <div className="p-6 text-center text-[12.5px] text-slate-500 dark:text-slate-400" data-testid="payments-no-match">No payments match this filter.</div>
      ) : (
        <>
          <div className="flex gap-2.5 px-4 py-1.5 text-[10.5px] font-bold uppercase tracking-wide text-slate-400 dark:text-slate-500 border-b border-slate-200 dark:border-slate-700">
            <span className="w-28">Receipt no.</span>
            <span className="w-24 hidden sm:block">Date</span>
            <span className="w-16 hidden md:block">Mode</span>
            <span className="flex-1">By / reason</span>
            <span className="w-24 text-right">Amount</span>
            <span className="w-24 text-right hidden lg:block">Held</span>
            <span className="w-32" />
          </div>
          {model.visibleRows.map((row) => (
            <PaymentRow
              key={row.payment_id}
              row={row}
              canRefund={canRefund}
              canPrint={canPrint}
              onRefund={onRefund}
              onPrint={onPrint}
            />
          ))}
          {model.hiddenCount > 0 && (
            <div className="px-4 py-3 border-b border-slate-200 dark:border-slate-700">
              <button type="button" onClick={() => setShowAll(true)} className="w-full text-center px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100 text-[12.5px] font-medium hover:bg-slate-100 dark:hover:bg-slate-700" data-testid="payments-expander">
                Show {model.hiddenCount} earlier entr{model.hiddenCount === 1 ? 'y' : 'ies'}
              </button>
            </div>
          )}
          {showAll && !model.isFiltering && (
            <div className="px-4 py-3 border-b border-slate-200 dark:border-slate-700">
              <button type="button" onClick={() => setShowAll(false)} className="w-full text-center px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100 text-[12.5px] font-medium hover:bg-slate-100 dark:hover:bg-slate-700" data-testid="payments-collapse">
                Hide earlier entries
              </button>
            </div>
          )}
        </>
      )}

      {/* Section money totals — server figures, single source. */}
      {!model.isEmpty && (
        <div className="p-4 bg-slate-50 dark:bg-slate-900 text-[13px]" data-testid="deposits-totals">
          <div className="flex justify-between py-0.5 text-slate-700 dark:text-slate-200">
            <span>Deposits collected ({model.depositCount} receipt{model.depositCount === 1 ? '' : 's'})</span>
            <span className="tabular-nums font-semibold text-slate-900 dark:text-white">{formatPaise(depositTotalPaise)}</span>
          </div>
          {refundedPaise > 0 && (
            <div className="flex justify-between py-0.5 text-red-600 dark:text-red-400">
              <span>Refunds paid ({model.refundCount} voucher{model.refundCount === 1 ? '' : 's'})</span>
              <span className="tabular-nums font-semibold">− {formatPaise(refundedPaise)}</span>
            </div>
          )}
          <div className="flex justify-between items-center border-t border-slate-300 dark:border-slate-600 mt-1.5 pt-2 font-bold text-slate-900 dark:text-white">
            <span>Deposits held (net)</span>
            <span className="tabular-nums">{formatPaise(netHeldPaise)}</span>
          </div>
        </div>
      )}
    </div>
  );
}
